import { App } from "vue";
import PromisesUtil from "../../../src/shared/application/utils/promises-util";

export default {
	install: (app: App, options?: any): void => {
		app.config.globalProperties.$execute_promise_functions_in_batches = async (
			promise_functions: (() => Promise<any>)[],
			batch_size: number,
			promises_execution_data: any = null
		): Promise<any> => {
			return await PromisesUtil.execute_promise_functions_in_batches(
				promise_functions,
				batch_size,
				promises_execution_data
			);
		};

		app.config.globalProperties.$duplicate = <T>(variable: T): T => {
			// Return null or undefined directly as they do not require cloning
			if (variable === null || variable === undefined) {
				return variable;
			}

			// Use native structuredClone for deep copying while preserving data structures (Arrays, Dates, Maps, etc.)
			try {
				return structuredClone(variable);
			} catch (error) {
				// Fallback for non-cloneable objects or legacy browser environments
				return JSON.parse(JSON.stringify(variable));
			}
		};

		/**
		 * Lightens a hex color by mixing it toward white.
		 * Example: amount = 0.30 means "30% closer to white" (30% white + 70% original).
		 *
		 * Supported inputs: #RGB, #RRGGBB (with or without leading #)
		 * Output: #RRGGBB (uppercase)
		 *
		 * @param {string} hex_color - Hex color string (#RGB or #RRGGBB).
		 * @param {number} amount - Mix amount toward white in [0..1]. Default is 0.30.
		 * @returns {string} - Lightened hex color in #RRGGBB format.
		 */
		app.config.globalProperties.$lighter_hex_color = (
			hex_color: string,
			amount: number = 0.5
		): string => {
			/**
			 * Clamps a numeric value into the [0..1] range.
			 * @param {number} value - Any numeric value.
			 * @returns {number} - Clamped value between 0 and 1.
			 */
			const clamp_01 = (value: any): number => {
				const numeric_value = Number(value);

				// Ensure the input is a finite number
				if (!Number.isFinite(numeric_value)) {
					throw new TypeError("amount must be a finite number");
				}

				return Math.min(1, Math.max(0, numeric_value));
			};

			// Ensure the input is a string
			if (typeof hex_color !== "string") {
				throw new TypeError("hex_color must be a string");
			}

			// Remove whitespace and an optional leading '#'
			let normalized_hex = hex_color.trim().replace(/^#/, "");

			// Expand short form #RGB into #RRGGBB
			if (normalized_hex.length === 3) {
				normalized_hex = normalized_hex
					.split("")
					.map((ch) => ch + ch)
					.join("");
			}

			// Validate the final hex format
			if (
				normalized_hex.length !== 6 ||
				!/^[0-9a-fA-F]{6}$/.test(normalized_hex)
			) {
				throw new Error("Invalid hex format. Use #RGB or #RRGGBB");
			}

			// Clamp amount to [0..1]
			const mix_amount = clamp_01(amount);

			// Parse RGB channels
			const red = parseInt(normalized_hex.slice(0, 2), 16);
			const green = parseInt(normalized_hex.slice(2, 4), 16);
			const blue = parseInt(normalized_hex.slice(4, 6), 16);

			// Mix each channel toward white (255)
			const new_red = Math.round(red + (255 - red) * mix_amount);
			const new_green = Math.round(green + (255 - green) * mix_amount);
			const new_blue = Math.round(blue + (255 - blue) * mix_amount);

			// Convert back to #RRGGBB
			return (
				"#" +
				[new_red, new_green, new_blue]
					.map((channel) => channel.toString(16).padStart(2, "0"))
					.join("")
					.toUpperCase()
			);
		};
	}
};

declare module "@vue/runtime-core" {
	interface ComponentCustomProperties {
		$execute_promise_functions_in_batches: (
			promise_functions: (() => Promise<any>)[],
			batch_size: number,
			promises_execution_data?: any
		) => Promise<any>;
		$duplicate: (variable: any) => any;
		$lighter_hex_color: (hex_color: string, amount?: number) => string;
	}
}
